import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, ExternalLink, Flex, Grid, Text } from 'src/packages/core';
import { DarkTheme, LightTheme, ThemeType, useTheme } from 'src/packages/theme';
import { PasswordHideIcon, PasswordShowIcon, ErrorIcon } from 'src/packages/icons';

import { AssetsLinks } from '../links';
import { HeaderAssets } from '../Header.types';

import { goToUrl } from 'src/utils/go-to-url';
import { config } from 'src/config';

interface AssetsPopoverContentProps {
  assets?: HeaderAssets;
  theme: ThemeType;
}

export const AssetsPopoverContent = ({ assets, theme }: AssetsPopoverContentProps) => {
  const { t } = useTranslation();
  const contextTheme = useTheme();

  const defaultTheme = useMemo(() => {
    return theme
      ? theme === 'light'
        ? { ...LightTheme, rtl: contextTheme.rtl }
        : { ...DarkTheme, rtl: contextTheme.rtl }
      : contextTheme;
  }, [contextTheme, theme]);

  return useMemo(
    () => (
      <Flex width={'100%'} flexDirection={'column'}>
        {!!assets && (
          <Flex
            p={5}
            width={'100%'}
            flexDirection={'column'}
            backgroundColor={defaultTheme.colors.background.secondary.BG4}
            gap={1}
          >
            <Flex width={'100%'} alignItems={'center'} justifyContent={'space-between'}>
              <Text
                variant={'p2'}
                text={t('total_assets')}
                color={
                  theme === 'light' ? LightTheme.colors.text.primary : DarkTheme.colors.text.primary
                }
              />

              <Flex onClick={assets.toggleShowBalance} style={{ cursor: 'pointer' }}>
                {!!assets.showBalance ? (
                  <PasswordHideIcon
                    size={14}
                    color={
                      theme === 'light'
                        ? LightTheme.colors.text.tertiary
                        : DarkTheme.colors.text.tertiary
                    }
                  />
                ) : (
                  <PasswordShowIcon
                    size={14}
                    color={
                      theme === 'light'
                        ? LightTheme.colors.text.tertiary
                        : DarkTheme.colors.text.tertiary
                    }
                  />
                )}
              </Flex>
            </Flex>

            <Flex alignItems={'center'} gap={1}>
              <Text
                variant={'p2'}
                fontFamily={'Inter-Medium'}
                text={!assets?.total ? '--' : `${!!assets.showBalance ? assets.total : '********'}`}
                color={
                  theme === 'light'
                    ? LightTheme.colors.text.primary
                    : DarkTheme.colors.palette.white
                }
              />
              {assets?.unit && (
                <Text
                  overrideRtl={true}
                  variant={'p2'}
                  fontFamily={'Inter-Medium'}
                  text={`${assets.unit}`}
                  color={
                    theme === 'light'
                      ? LightTheme.colors.text.primary
                      : DarkTheme.colors.palette.white
                  }
                />
              )}
              {!!assets?.error && (
                <ErrorIcon
                  size={14}
                  color={contextTheme.colors.palette.red}
                  style={{ marginLeft: 4 }}
                />
              )}
            </Flex>

            <Flex alignItems={'center'} gap={1}>
              <Text
                variant={'p3'}
                mb={2}
                text={
                  !assets?.total ? '-- ≈' : `≈ ${!!assets.showBalance ? assets.total : '********'}`
                }
                color={
                  theme === 'light'
                    ? LightTheme.colors.text.tertiary
                    : DarkTheme.colors.text.secondary
                }
              />
              {assets?.unit && (
                <Text
                  overrideRtl={true}
                  variant={'p3'}
                  mb={2}
                  text={`${assets.unit}`}
                  color={
                    theme === 'light'
                      ? LightTheme.colors.text.tertiary
                      : DarkTheme.colors.text.secondary
                  }
                />
              )}
            </Flex>

            <Grid width={'100%'} gap={1} gridTemplateColumns={'1fr 1fr'} gridTemplateRows={'1fr'}>
              <Button
                variant={'brandSmall'}
                text={t('button.deposit')}
                width={'100%'}
                onClick={() => {
                  goToUrl(`${config.coinflareUrl}/assets/spot/deposit`);
                }}
              />
              <Button
                variant={'secondarySmall'}
                text={t('button.withdraw')}
                width={'100%'}
                onClick={() => {
                  goToUrl(`${config.coinflareUrl}/assets/spot/withdraw`);
                }}
              />
            </Grid>
          </Flex>
        )}

        <ul>
          {AssetsLinks.map((item, index) => (
            <ExternalLink key={index} link={item.url}>
              <Flex width={'100%'} alignItems={'center'}>
                <Text
                  variant={'p2'}
                  color={
                    theme === 'light'
                      ? LightTheme.colors.text.primary
                      : DarkTheme.colors.text.primary
                  }
                  text={t(item.label)}
                />
              </Flex>
            </ExternalLink>
          ))}
        </ul>
      </Flex>
    ),
    [assets, theme]
  );
};
